import { motion } from "framer-motion";
import { useMemo } from "react";
import { tierProgress } from "../../lib/walletTier";

const inr = (n) => `₹${Number(Math.round(n) || 0).toLocaleString("en-IN")}`;

/**
 * LiquidBalance
 * ─────────────
 * A ~260px circular medallion that renders the wallet balance with a
 * living, breathing liquid wave inside. The wave fill height tracks the
 * buyer's progress through their current tier band so climbing tiers
 * visibly "fills up" the vault.
 *
 * Pure SVG + Framer Motion — no canvas, respects reduced motion.
 */
export function LiquidBalance({ balance, tier, size = 260 }) {
  const progress = tierProgress(balance);
  // Wave lives at 78% -> 22% of the circle depending on progress.
  const level = 0.78 - progress * 0.56;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 8;
  const waveTop = cy - r + level * (r * 2);

  // Deterministic wave paths so the ripple loop is silky-smooth.
  const wave1 = useMemo(
    () => buildWavePath(0, size, waveTop, 12, 0),
    [size, waveTop],
  );
  const wave2 = useMemo(
    () => buildWavePath(0, size, waveTop + 6, 8, size / 2),
    [size, waveTop],
  );

  return (
    <div
      data-testid="liquid-balance"
      className="relative"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <clipPath id="lb-clip">
            <circle cx={cx} cy={cy} r={r} />
          </clipPath>
          <radialGradient id="lb-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={tier.primary} stopOpacity="0.35" />
            <stop offset="60%" stopColor={tier.primary} stopOpacity="0.05" />
            <stop offset="100%" stopColor="#000" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="lb-liquid" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={tier.accent} stopOpacity="0.9" />
            <stop offset="100%" stopColor={tier.primary} stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id="lb-liquid2" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={tier.primary} stopOpacity="0.7" />
            <stop offset="100%" stopColor={tier.accentSoft} stopOpacity="0.35" />
          </linearGradient>
        </defs>

        {/* outer aura */}
        <circle cx={cx} cy={cy} r={r + 6} fill="url(#lb-glow)" />

        {/* obsidian ring */}
        <circle
          cx={cx} cy={cy} r={r}
          fill="#050505"
          stroke={tier.primary}
          strokeOpacity="0.55"
          strokeWidth="1.5"
        />

        {/* liquid inside the medallion */}
        <g clipPath="url(#lb-clip)">
          <motion.path
            d={wave2}
            fill="url(#lb-liquid2)"
            animate={{ x: [0, -size, 0] }}
            transition={{ duration: 9, ease: "linear", repeat: Infinity }}
          />
          <motion.path
            d={wave1}
            fill="url(#lb-liquid)"
            animate={{ x: [0, size, 0] }}
            transition={{ duration: 7, ease: "linear", repeat: Infinity }}
          />
        </g>

        {/* inner ring highlight */}
        <circle
          cx={cx} cy={cy} r={r - 2}
          fill="none"
          stroke={tier.accent}
          strokeOpacity="0.18"
          strokeWidth="0.75"
        />
      </svg>

      {/* Balance figure */}
      <div className="absolute inset-0 grid place-items-center pointer-events-none">
        <div className="text-center">
          <div
            className="font-grotesk text-[9px] uppercase tracking-[0.42em]"
            style={{ color: tier.accentSoft }}
          >
            Available
          </div>
          <div
            data-testid="wallet-balance"
            className="font-serif text-4xl md:text-5xl tracking-tight mt-1"
            style={{
              color: "#F5F5F0",
              textShadow: `0 0 40px ${tier.glow}`,
            }}
          >
            {inr(balance || 0)}
          </div>
          <div
            className="font-grotesk text-[9px] uppercase tracking-[0.36em] mt-2"
            style={{ color: tier.accent }}
          >
            {tier.label}
          </div>
        </div>
      </div>
    </div>
  );
}

// Builds a wide sinusoidal wave path 2× the SVG width so the horizontal
// translate animation can loop seamlessly.
function buildWavePath(x0, width, y, amplitude, phase) {
  const steps = 24;
  const w2 = width * 2;
  let d = `M ${x0 - width} ${y}`;
  for (let i = 0; i <= steps; i += 1) {
    const t = (i / steps) * Math.PI * 4;
    const px = x0 - width + (i / steps) * w2;
    const py = y + Math.sin(t + phase) * amplitude;
    d += ` L ${px.toFixed(2)} ${py.toFixed(2)}`;
  }
  d += ` L ${x0 + width} ${width * 2} L ${x0 - width} ${width * 2} Z`;
  return d;
}
